import { Injectable } from '@angular/core';
import { PopupService } from '../popup/popup.service';
import { PopupOptions } from '../popup/popup-options';
import { RaiseService } from '../../../core/services/api/raise.service';
import { ErrorService } from '../../../core/services/error.service';

@Injectable({ providedIn: 'root' })
export class SubFormHandlerService {
    activeSubFormQuestionKey: string = null;
    activeSubFormKey: string = null;
    readonly subFormItemMarker = '__active_subform_item__';
    private readonly subFormLoadTokens = new Map<string, number>();

    constructor(
        private popupService: PopupService,
        private raiseService: RaiseService,
        private errorService: ErrorService,
    ) {}

    onSubFormTriggered(
        event: any,
        form: any | (() => any),
        parseTemplateFn: (result: any) => any,
        options?: {
            blockSubFormChanges?: boolean;
            readonlyItems?: boolean;
            onFormUpdated?: (form: any) => void;
        },
    ): void {
        // Accept a getter so async responses apply to the latest form state, not a stale snapshot.
        const getForm = () => (typeof form === 'function' ? form() : form);

        if (options?.blockSubFormChanges) {
            event?.revertOptionChange?.();
            return;
        }

        const questionKey = event?.question?.key;
        const subForm = event?.subForm || event?.selectedOption?.subForm;

        const subFormId =
            event?.subFormId ||
            subForm?.id ||
            subForm?.dynamicFormTemplateId;

        // Any newer trigger for the same scope invalidates an in-flight sub-form load.
        const { key: loadKey, token: loadToken } = this.nextSubFormLoadGeneration(event, questionKey, subFormId);
        const isStale = () => this.subFormLoadTokens.get(loadKey) !== loadToken;

        if (event?.isSubFormRemoval) {
            const updatedForm = this.removeSubFormItemsById(subFormId, questionKey, getForm());
            event?.confirmOptionChange?.();
            options?.onFormUpdated?.(updatedForm);
            return;
        }

        if (!subForm) {
            const currentForm = getForm();
            const hasSubFormItems = currentForm?.formPanels?.some((panel: any) =>
                panel.items?.some(
                    (item: any) =>
                        item?._subFormItemMarker === this.subFormItemMarker &&
                        item?._subFormQuestionKey === questionKey,
                ),
            );

            if (!hasSubFormItems) {
                event?.confirmOptionChange?.();
                return;
            }

            const updatedForm = this.removeSubFormItemsByQuestionKey(questionKey, currentForm);
            event?.confirmOptionChange?.();
            options?.onFormUpdated?.(updatedForm);
            return;
        }

        if (!subFormId) {
            event?.revertOptionChange?.();
            return;
        }

        this.raiseService.getPublicDynamicFormTemplateById(subFormId).subscribe(
            (result) => {
                if (isStale()) {
                    return;
                }

                const subFormTemplate = parseTemplateFn(result);

                if (!subFormTemplate?.formPanels?.length) {
                    return;
                }

                const updatedForm = this.insertSubFormItems(event, subFormTemplate, subFormId, getForm(), {
                    readonlyItems: options?.readonlyItems,
                });

                // insertSubFormItems() confirms the option change itself.
                options?.onFormUpdated?.(updatedForm);
            },
            (err) => {
                if (isStale()) {
                    return;
                }

                if (Array.isArray(event?.selectedValue) && event?.removeFailedOption) {
                    // Multi-select: drop only the failed option (and any fields it inserted);
                    // reverting to the captured selection would also discard sibling options
                    // whose own loads are still current.
                    event.removeFailedOption();
                    const currentForm = getForm();
                    const updatedForm = this.removeSubFormItemsById(subFormId, questionKey, currentForm);
                    if (this.hasSubFormItemsFor(currentForm, questionKey, subFormId)) {
                        options?.onFormUpdated?.(updatedForm);
                    }
                } else {
                    event?.revertOptionChange?.();
                }
                this.errorService.showError(err);
            },
        );
    }

    /**
     * Bumps the load generation for a sub-form trigger. Single-select replaces its
     * selection, so the generation is question-wide; multi-select events are per option,
     * so the generation is scoped to question + sub-form id (mirrors AdminWeb GRC-2222).
     */
    private nextSubFormLoadGeneration(event: any, questionKey: any, subFormId: any): { key: string; token: number } {
        const key = Array.isArray(event?.selectedValue) ? `${questionKey}::${subFormId}` : `${questionKey}`;
        const token = (this.subFormLoadTokens.get(key) ?? 0) + 1;
        this.subFormLoadTokens.set(key, token);
        return { key, token };
    }

    private hasSubFormItemsFor(form: any, questionKey: any, subFormId: any): boolean {
        const subFormKey = subFormId?.toString();
        return !!form?.formPanels?.some((panel: any) =>
            panel.items?.some(
                (item: any) =>
                    item?._subFormItemMarker === this.subFormItemMarker &&
                    item?._subFormQuestionKey === questionKey &&
                    item?._subFormId === subFormKey,
            ),
        );
    }

    insertSubFormItems(
        event: any,
        subFormTemplate: any,
        subFormId: any,
        form: any,
        options?: { readonlyItems?: boolean },
    ): any {
        const questionKey = event?.question?.key;

        if (!questionKey || !form?.formPanels) {
            return form;
        }

        const newSubFormKey = subFormId?.toString();

        const shouldKeepOtherSubForms = Array.isArray(event?.selectedValue);

        const removedKeys: string[] = [];

        const cleanedPanels = form.formPanels.map((panel: any) => ({
            ...panel,
            items: (panel.items || []).filter((item: any) => {
                if (item?._subFormItemMarker !== this.subFormItemMarker) {
                    return true;
                }

                const isSameQuestionSubFormItem = item?._subFormQuestionKey === questionKey;
                const isSameSubFormItem = item?._subFormId === newSubFormKey;

                const shouldRemove = shouldKeepOtherSubForms
                    ? isSameQuestionSubFormItem && isSameSubFormItem
                    : isSameQuestionSubFormItem;

                if (shouldRemove && item.key) {
                    removedKeys.push(item.key);
                }

                if (shouldKeepOtherSubForms) {
                    return !isSameQuestionSubFormItem || !isSameSubFormItem;
                }

                return !isSameQuestionSubFormItem;
            }),
        }));

        const triggerPanelIndex = cleanedPanels.findIndex((panel: any) =>
            panel.items?.some((item: any) => item.key === questionKey),
        );

        if (triggerPanelIndex < 0) {
            return form;
        }

        const triggerPanel = cleanedPanels[triggerPanelIndex];

        const triggerItemIndex = triggerPanel.items.findIndex(
            (item: any) => item.key === questionKey,
        );

        if (triggerItemIndex < 0) {
            return form;
        }

        const flattenedSubFormItems = (subFormTemplate.formPanels || []).reduce(
            (items: any[], panel: any) => {
                return items.concat(panel.items || []);
            },
            [],
        );

        const optionKey = event?.selectedOption?.key?.toString() ?? event?.selectedOption?.value?.toString() ?? null;

        const subFormItems = flattenedSubFormItems.map(
            (item: any, index: number) => {
                const clonedItem = JSON.parse(JSON.stringify(item));

                const originalKey = clonedItem.key;

                if (optionKey) {
                    clonedItem.key = `${questionKey}__${optionKey}__${originalKey}`;
                } else {
                    clonedItem.key = `${questionKey}__${originalKey}`;
                }

                if (options?.readonlyItems) {
                    clonedItem.readonly = true;
                }

                return {
                    ...clonedItem,
                    _originalKey: originalKey,
                    _subFormItemMarker: this.subFormItemMarker,
                    _subFormId: newSubFormKey,
                    _subFormQuestionKey: questionKey,
                    _isSubFormItem: true,
                    _isFirstSubFormItem: index === 0,
                    _isLastSubFormItem:
                        index === flattenedSubFormItems.length - 1,
                };
            },
        );

        triggerPanel.items = [
            ...triggerPanel.items.slice(0, triggerItemIndex + 1),
            ...subFormItems,
            ...triggerPanel.items.slice(triggerItemIndex + 1),
        ];

        const updatedForm = { ...form, formPanels: cleanedPanels };

        const cleanedValues = { ...(event?.currentValues || {}) };
        removedKeys.forEach(key => delete cleanedValues[key]);
        // Multi-select: the captured snapshot may still list a sibling option that failed
        // since; keep the trigger question in step with the live control value.
        if (shouldKeepOtherSubForms && event?.getCurrentSelection) {
            cleanedValues[questionKey] = event.getCurrentSelection();
        }
        this.applyCurrentValues(cleanedValues, updatedForm);

        this.activeSubFormQuestionKey = questionKey;
        this.activeSubFormKey = newSubFormKey;

        event?.confirmOptionChange?.();

        return updatedForm;
    }

    removeSubFormItemsById(subFormId: any, questionKey: any, form: any): any {
        const subFormKey = subFormId?.toString();

        if (!subFormKey || !form?.formPanels) {
            return form;
        }

        const updatedForm = {
            ...form,
            formPanels: form.formPanels.map((panel: any) => ({
                ...panel,
                items: (panel.items || []).filter(
                    (item: any) =>
                        item?._subFormItemMarker !== this.subFormItemMarker ||
                        item?._subFormId !== subFormKey ||
                        (!!questionKey && item?._subFormQuestionKey !== questionKey),
                ),
            })),
        };

        if (
            this.activeSubFormKey === subFormKey &&
            (!questionKey || this.activeSubFormQuestionKey === questionKey)
        ) {
            this.activeSubFormQuestionKey = null;
            this.activeSubFormKey = null;
        }

        return updatedForm;
    }

    removeSubFormItemsByQuestionKey(questionKey: any, form: any): any {
        if (!questionKey || !form?.formPanels) {
            return form;
        }

        const updatedForm = {
            ...form,
            formPanels: form.formPanels.map((panel: any) => ({
                ...panel,
                items: (panel.items || []).filter(
                    (item: any) =>
                        item?._subFormItemMarker !== this.subFormItemMarker ||
                        item?._subFormQuestionKey !== questionKey,
                ),
            })),
        };

        if (this.activeSubFormQuestionKey === questionKey) {
            this.activeSubFormQuestionKey = null;
            this.activeSubFormKey = null;
        }

        return updatedForm;
    }

    applyCurrentValues(values: any, form: any): void {
        if (!values || !form?.formPanels) {
            return;
        }

        form.formPanels.forEach((formPanel: any) => {
            formPanel.items?.forEach((item: any) => {
                if (!item?.key || !(item.key in values)) {
                    return;
                }

                item.value = values[item.key];
            });
        });
    }
}
